/**
 * Table Page Skeleton
 *
 * Skeleton for table-based list pages (Prompts, Projects, Models, Tools, etc.)
 * Mirrors actual page layout for smooth transition when real content loads.
 *
 * Design Philosophy:
 * - Match real content structure (header, toolbar, table, pagination)
 * - User recognizes layout immediately
 * - Smooth transition to real content (no layout shift)
 *
 * @module TablePageSkeleton
 */

import { memo } from 'react'
import { Skeleton } from '../ui/Skeleton'
import './TablePageSkeleton.css'

/**
 * Table Page Skeleton Props
 */
export interface TablePageSkeletonProps {
  /** Number of table rows to show (default: 10) */
  rows?: number

  /** Show page header (default: true) */
  showHeader?: boolean

  /** Show toolbar with search/filters (default: true) */
  showToolbar?: boolean

  /** Show pagination (default: true) */
  showPagination?: boolean
}

/**
 * Table Page Skeleton
 *
 * A complete page skeleton for table/list views.
 * Matches the structure of actual pages for seamless transition.
 *
 * Layout Structure:
 * 1. Page Header (title + action button)
 * 2. Toolbar (search bar + filter buttons)
 * 3. Table (header + rows)
 * 4. Pagination (page info + controls)
 *
 * Used by: Prompts, Projects, Models, Tools, Connections, Admin pages
 *
 * @example
 * ```tsx
 * // In App.tsx Suspense fallback
 * <Suspense fallback={<TablePageSkeleton />}>
 *   <PromptsPage />
 * </Suspense>
 * ```
 */
// Memoize to prevent unnecessary re-renders
export const TablePageSkeleton = memo(function TablePageSkeleton({
  rows = 10,
  showHeader = true,
  showToolbar = true,
  showPagination = true,
}: TablePageSkeletonProps = {}) {
  // Pre-compute row array to avoid recreating on each render
  const rowArray = Array.from({ length: rows }, (_, i) => i)

  return (
    <div className="table-page-skeleton" role="status" aria-label="Loading page content">
      {/* Page Header */}
      {showHeader && (
        <div className="table-page-skeleton__header">
          {/* Page Title */}
          <Skeleton variant="rectangular" width={200} height={28} />

          {/* Primary Action Button (e.g., "Add New") */}
          <Skeleton variant="rectangular" width={120} height={36} />
        </div>
      )}

      {/* Toolbar (Search + Filters) */}
      {showToolbar && (
        <div className="table-page-skeleton__toolbar">
          {/* Search Bar */}
          <Skeleton variant="rectangular" width={300} height={36} />

          {/* Toolbar Actions (filters, export, refresh) */}
          <div className="table-page-skeleton__toolbar-actions">
            <Skeleton variant="rectangular" width={100} height={32} />
            <Skeleton variant="rectangular" width={32} height={32} />
            <Skeleton variant="rectangular" width={32} height={32} />
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className="table-page-skeleton__table-container">
        {/* Table Header Row */}
        <div className="table-page-skeleton__table-header">
          <Skeleton variant="rectangular" width="100%" height={40} />
        </div>

        {/* Table Body - Rows */}
        <div className="table-page-skeleton__table-body">
          {rowArray.map((i) => (
            <div key={i} className="table-page-skeleton__row">
              <Skeleton variant="rectangular" width="100%" height={56} />
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {showPagination && (
        <div className="table-page-skeleton__pagination">
          {/* Page Info (e.g., "Showing 1-20 of 100") */}
          <Skeleton variant="rectangular" width={150} height={32} />

          {/* Page Controls (prev, 1, 2, 3, next) */}
          <div className="table-page-skeleton__page-controls">
            <Skeleton variant="rectangular" width={32} height={32} />
            <Skeleton variant="rectangular" width={32} height={32} />
            <Skeleton variant="rectangular" width={32} height={32} />
            <Skeleton variant="rectangular" width={32} height={32} />
            <Skeleton variant="rectangular" width={32} height={32} />
          </div>
        </div>
      )}
    </div>
  )
})

/**
 * Why these specific dimensions?
 *
 * - Header title (200x28px): Typical page title size
 * - Action button (120x36px): Standard button size
 * - Search bar (300x36px): Wide enough for placeholder text
 * - Table row (100%x56px): Matches actual DataTable row height
 * - Pagination (150x32px + controls): Matches real pagination
 *
 * Goal: When real content loads, it should fit exactly into skeleton space
 * Result: Zero layout shift (CLS = 0)
 */

/**
 * Performance Considerations:
 *
 * 1. Number of skeleton elements:
 *    - Default 10 rows = ~20 skeleton components
 *    - Each skeleton is lightweight (<1KB)
 *    - Total render time: <10ms
 *    - Acceptable for loading state
 *
 * 2. Memory footprint:
 *    - Simple divs with CSS
 *    - No images or heavy assets
 *    - Negligible memory usage
 *
 * 3. Animation performance:
 *    - CSS animations (GPU accelerated)
 *    - No JavaScript
 *    - 60fps on all devices
 */

/**
 * Customization Guide:
 *
 * For different page types:
 *
 * 1. Grid view (cards):
 *    - Use GridPageSkeleton instead
 *    - Show card grid instead of table rows
 *
 * 2. Detail page:
 *    - Use DetailPageSkeleton
 *    - Show form fields instead of table
 *
 * 3. Dashboard:
 *    - Use DashboardSkeleton
 *    - Show stat cards + charts
 *
 * This skeleton is specifically for list/table views.
 */
